import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildingsApiNew as buildingsApi, CreateBuildingDto, Operator } from '../../api';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import { getApiErrorMessage, parseApiFieldErrors, FieldErrors } from '../../utils/apiErrors';
import { fieldInputClass, prepareBuildingPayload, validateBuildingForm } from '../../utils/buildingForm';
import { BuildingLogoField } from '../../components/buildings/BuildingLogoField';
import { ParkingLegacyApiKeyField } from '../../components/buildings/ParkingLegacyApiKeyField';

export const AddBuilding: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = useFunctionPermissions();
  const canCreateBuilding = can('BUILDING_ADD', 'create');
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState<CreateBuildingDto>({
    name: '',
    projectCode: '',
    managementPhone: '',
    managementEmail: '',
    logoPath: '',
    address: '',
    isActive: true,
    allowOnlinePayment: false,
    allowPartialPayment: false,
    autoSendEmailReminder: false,
    autoGenerateMonthlyServiceFees: true,
    serviceFeeDay: 5,
    paymentFromDay: 10,
    paymentToDay: 20,
    operatorId: '',
    parkingLegacyApiKey: '',
  });

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOperators();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!permissionsLoading && !canCreateBuilding) {
      navigate('/buildings');
    }
  }, [permissionsLoading, canCreateBuilding, navigate]);

  const fetchOperators = async () => {
    setLoadingOperators(true);
    try {
      const data = await buildingsApi.getOperators();
      setOperators(data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setFormError('');
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number' || name === 'serviceFeeDay' || name === 'paymentFromDay' || name === 'paymentToDay') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFieldErrors({});

    if (isSuperAdmin && !formData.operatorId) {
      setFieldErrors({ operatorId: t('pleaseSelectOperator') });
      setFormError(t('pleaseFixFormErrors'));
      return;
    }

    const clientErrors = validateBuildingForm(formData, t('invalidEmail'), t('invalidProjectCode'));
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError(t('pleaseFixFormErrors'));
      return;
    }
    
    setLoading(true);

    try {
      const submitData = prepareBuildingPayload({ ...formData });
      if (!isSuperAdmin) {
        delete submitData.operatorId;
      }
      
      await buildingsApi.createBuilding(submitData);
      navigate('/buildings');
    } catch (error) {
      console.error('Error creating building:', error);
      const apiFieldErrors = parseApiFieldErrors(error);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
        setFormError(t('pleaseFixFormErrors'));
      } else {
        setFormError(getApiErrorMessage(error, t('errorCreatingBuilding')));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/buildings')}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <nav className="text-sm">
                <span className="opacity-75">🏠 / {t('buildings')} / </span>
                <span>{t('addBuilding')}</span>
              </nav>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <span className="text-sm">👤</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Building Information */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-3 border-b border-cyan-200">
                {t('buildingGeneralInfo')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('buildingName')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('enterBuildingName')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('projectCode')}
                  </label>
                  <input
                    type="text"
                    name="projectCode"
                    value={formData.projectCode || ''}
                    onChange={handleInputChange}
                    placeholder={t('enterProjectCode')}
                    className={fieldInputClass(!!fieldErrors.projectCode)}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('projectCodeHint')}</p>
                  {fieldErrors.projectCode && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.projectCode}</p>
                  )}
                </div>

                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('operator')} *
                    </label>
                    {loadingOperators ? (
                      <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                        {t('loading')}...
                      </div>
                    ) : (
                      <select
                        name="operatorId"
                        value={formData.operatorId}
                        onChange={handleInputChange}
                        className={fieldInputClass(!!fieldErrors.operatorId)}
                        required
                      >
                        <option value="">{t('selectOperator')}</option>
                        {operators.map(operator => (
                          <option key={operator.id} value={operator.id}>
                            {operator.name} ({operator.email})
                          </option>
                        ))}
                      </select>
                    )}
                    {fieldErrors.operatorId && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.operatorId}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('managementPhone')}
                  </label>
                  <input
                    type="tel"
                    name="managementPhone"
                    value={formData.managementPhone}
                    onChange={handleInputChange}
                    placeholder={t('enterManagementPhone')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('managementEmail')}
                  </label>
                  <p className="mb-2 text-xs text-gray-500">{t('managementEmailOptionalHint')}</p>
                  <input
                    type="email"
                    name="managementEmail"
                    value={formData.managementEmail}
                    onChange={handleInputChange}
                    placeholder={t('enterManagementEmail')}
                    className={fieldInputClass(!!fieldErrors.managementEmail)}
                  />
                  {fieldErrors.managementEmail && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.managementEmail}</p>
                  )}
                </div>

                <BuildingLogoField
                  logoPath={formData.logoPath || ''}
                  onLogoPathChange={(logoPath) => setFormData((prev) => ({ ...prev, logoPath }))}
                  operatorId={isSuperAdmin ? formData.operatorId : undefined}
                  uploadDisabled={isSuperAdmin && !formData.operatorId}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('address')}
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>{loading ? t('savingBuilding') : t('saveBuilding')}</span>
                </button>
              </form>
            </div>
          </div>

          {/* Right Column - System Configuration */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-3 border-b border-cyan-200">
                {t('systemConfiguration')}
              </h2>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center text-blue-700 mb-3">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium">{t('configurationNote')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{t('isActive')}</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="allowOnlinePayment"
                      checked={formData.allowOnlinePayment}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-green-500 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{t('allowOnlinePayment')}</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="allowPartialPayment"
                      checked={formData.allowPartialPayment}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-gray-400 border-gray-300 rounded focus:ring-gray-400"
                    />
                    <span className="ml-3 text-sm text-gray-700">{t('allowPartialPayment')}</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="autoSendEmailReminder"
                      checked={formData.autoSendEmailReminder}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">{t('autoSendEmailReminder')}</span>
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="autoGenerateMonthlyServiceFees"
                      checked={formData.autoGenerateMonthlyServiceFees !== false}
                      onChange={handleInputChange}
                      className="mt-0.5 w-5 h-5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium block">{t('autoGenerateMonthlyServiceFees')}</span>
                      <span className="text-gray-500">{t('autoGenerateMonthlyServiceFeesHint')}</span>
                    </span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('serviceFeeDay')}
                    </label>
                    <select
                      name="serviceFeeDay"
                      value={formData.serviceFeeDay}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <ParkingLegacyApiKeyField
                  value={formData.parkingLegacyApiKey || ''}
                  onChange={(parkingLegacyApiKey) =>
                    setFormData((prev) => ({ ...prev, parkingLegacyApiKey }))
                  }
                  hasError={!!fieldErrors.parkingLegacyApiKey}
                  errorMessage={fieldErrors.parkingLegacyApiKey}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('paymentFromDay')}
                    </label>
                    <select
                      name="paymentFromDay"
                      value={formData.paymentFromDay}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('paymentToDay')}
                    </label>
                    <select
                      name="paymentToDay"
                      value={formData.paymentToDay}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 